import Flex from '@commons/containers/Flex';
import ResponsiveContainer from '@commons/containers/ResponsiveContainer';
import PageBuilder from '@commons/pagebuilder';
import type { DrupalSection } from '@commons/types/drupal';
import type { Breakpoint, ResponsiveStyle } from '@commons/types/styles';
import type { Alignment, Spacing, Theme, Width } from '@commons/types/styles';

import { Background, type BackgroundSettings } from '../Background';
import { type DrupalSectionFooter, renderFooterRegion } from '../Footer';
import { type DrupalSectionHeader, renderHeaderRegion } from '../Header';
import { type SectionMinHeightType, getSectionMinHeight } from '../helpers';
import ColumnRegion from './Column';

// Regions of the columns section.

const COLUMNS_SECTION_COLUMN_REGIONS = ['first', 'second', 'third', 'fourth'] as const;

const COLUMNS_SECTION_REGIONS = ['header', ...COLUMNS_SECTION_COLUMN_REGIONS, 'footer'];

// Type definition for the settings of a column region.

type RegionSettings = {
  width: ResponsiveStyle<Width>;
  horizontalAlignment: Alignment;
  verticalAlignment: Alignment;
};

// Type definition for the ColumnsSection component properties.

type Props = DrupalSection<{
  appearance: BackgroundSettings & {
    theme: Theme;
  };
  container: {
    contentWidth: Width;
    paddingTop: Spacing;
    paddingBottom: Spacing;
    horizontalAlignment: Alignment;
    verticalAlignment?: Alignment;
    minHeightType: SectionMinHeightType;
    customMinHeight?: string;
    fluid?: boolean;
  };
  layout: {
    columns: 1 | 2 | 3 | 4;
    columnGap: Spacing | 'max';
    mobileBreakpoint: Breakpoint;
    mobileReverse: boolean;
  };
  regions: {
    first: RegionSettings;
    second: RegionSettings;
    third: RegionSettings;
    fourth: RegionSettings;
  };
  header?: DrupalSectionHeader;
  footer?: DrupalSectionFooter;
}>;

/**
 * Columns section component.
 *
 * IMPORTANT:
 * - This component should be a server component, otherwise everything it renders and itself will be client components.
 *   Since it is used on almost every pages, this would bloat the JavaScript bundle significantly.
 */

const ColumnsSection = ({ type, blocks, weight, settings }: Props) => {
  const { appearance, container, layout, regions, header, footer } = settings;

  const {
    contentWidth,
    paddingTop,
    paddingBottom,
    horizontalAlignment,
    verticalAlignment,
    minHeightType,
    customMinHeight,
    fluid,
  } = container;

  const { columns, columnGap, mobileBreakpoint, mobileReverse } = layout;

  const responsiveContentWidth: ResponsiveStyle<Width> = {
    xs: 12,
    [mobileBreakpoint]: contentWidth,
  };

  const minHeight = getSectionMinHeight(minHeightType, customMinHeight);

  const sectionData = { type, weight };
  const blocksByRegions = PageBuilder.filterBlocksByRegions(blocks, COLUMNS_SECTION_REGIONS);

  const renderedHeader =
    header &&
    renderHeaderRegion(blocksByRegions.header, header, sectionData, {
      width: responsiveContentWidth,
    });

  const renderedFooter =
    footer &&
    renderFooterRegion(blocksByRegions.footer, footer, sectionData, {
      width: responsiveContentWidth,
    });

  const renderedColumns = COLUMNS_SECTION_COLUMN_REGIONS.map((region, index) => {
    if (index >= columns) return null;

    const renderedBlocks = blocksByRegions[region].map(PageBuilder.renderBlock);
    const { width, horizontalAlignment, verticalAlignment } = regions[region];

    return PageBuilder.renderRegion(
      region,
      ColumnRegion,
      {
        width,
        align: horizontalAlignment,
        justify: verticalAlignment,
        children: renderedBlocks,
      },
      sectionData
    );
  });

  // If the gap is set to 'max', leave a minimal gap between columns on mobile.
  const gap: ResponsiveStyle<Spacing> = {
    xs: columnGap !== 'max' ? columnGap : 'md',
    [mobileBreakpoint]: columnGap !== 'max' ? columnGap : undefined,
  };

  const isPriority = weight === 0;
  const renderedBackground = <Background priority={isPriority} {...appearance} />;

  return (
    <ResponsiveContainer
      fluid={fluid}
      theme={appearance.theme}
      paddingTop={paddingTop}
      paddingBottom={paddingBottom}
      horizontalAlignment={horizontalAlignment}
      verticalAlignment={verticalAlignment}
      outsideChildren={renderedBackground}
      className={`mobile-breakpoint-${mobileBreakpoint}`}
      style={{ minHeight }}
    >
      {renderedHeader}
      <Flex
        gap={gap}
        width={responsiveContentWidth}
        justify={columnGap === 'max' ? 'space-between' : 'center'}
        direction={{ xs: mobileReverse ? 'column-reverse' : 'column', [mobileBreakpoint]: 'row' }}
        className="columns"
      >
        {renderedColumns}
      </Flex>
      {renderedFooter}
    </ResponsiveContainer>
  );
};

export default ColumnsSection;
